import * as React from 'react'

import dompurify from 'dompurify'
import MarkdownIt from 'markdown-it'
import type {
  MarkdownIt as MarkdownItInstance,
  MarkdownItOptions,
} from 'markdown-it'

import type { JSX } from 'react'

const DEFAULT_OPTIONS: MarkdownItOptions = {
  html: false,
  linkify: true,
  breaks: true,
}

// markdown-it v15 disabled linkify-it's fuzzy-link matching by default, so
// bare domains like `buffer.com` (no scheme) would stop auto-linking unless
// re-enabled explicitly.
function withFuzzyLinkify(md: MarkdownItInstance): MarkdownItInstance {
  md.linkify.set({ fuzzyLink: true })
  return md
}

// Shared instance used when callers don't override options. Building a
// MarkdownIt instance is non-trivial so we avoid doing it per render.
const defaultMd = withFuzzyLinkify(new MarkdownIt('default', DEFAULT_OPTIONS))

export type MarkdownProps = Omit<
  React.ComponentPropsWithoutRef<'div'>,
  'children' | 'dangerouslySetInnerHTML'
> & {
  /** Markdown source string to render. */
  children: string
  /**
   * Optional markdown-it options. Defaults to a safe config
   * (`html: false`, `linkify: true`, `breaks: true`). When provided, a new
   * MarkdownIt instance is built and memoized on the options reference.
   */
  options?: MarkdownItOptions
}

/**
 * Renders a markdown string as sanitized HTML.
 *
 * Parses with `markdown-it` (raw HTML disabled by default) and then runs
 * the output through `DOMPurify` as a defence-in-depth layer. Any native
 * `<div>` attribute (className, style, aria-*, data-*, onClick, …) can be
 * passed as props.
 */
export function Markdown({
  children,
  options,
  ...rest
}: MarkdownProps): JSX.Element {
  const md = React.useMemo(
    () =>
      options
        ? withFuzzyLinkify(new MarkdownIt('default', options))
        : defaultMd,
    [options],
  )

  const html = React.useMemo(
    () => dompurify.sanitize(md.render(children ?? '')),
    [md, children],
  )

  return <div {...rest} dangerouslySetInnerHTML={{ __html: html }} />
}
